/**
 * MermaidDiagram Component
 * Renders Mermaid diagrams visually using the mermaid library API
 *
 * Uses mermaid.render() directly instead of auto-scan for React compatibility
 * See: https://mermaid.js.org/config/usage.html
 */

import React, { useState, useLayoutEffect, useRef, useId, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import mermaid from 'mermaid';

// Initialize mermaid with configuration
mermaid.initialize({
  startOnLoad: false, // We'll render manually
  theme: 'default',
  securityLevel: 'strict',
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  flowchart: {
    htmlLabels: true,
    curve: 'basis',
    padding: 15,
    nodeSpacing: 50,
    rankSpacing: 50,
  },
  themeVariables: {
    primaryColor: '#3b82f6',
    primaryTextColor: '#1f2937',
    primaryBorderColor: '#2563eb',
    lineColor: '#6b7280',
    secondaryColor: '#f3f4f6',
    tertiaryColor: '#f9fafb',
  },
});

interface MermaidDiagramProps {
  chart: string;
  className?: string;
  onError?: (error: Error) => void;
}

interface RenderState {
  svg: string | null;
  error: Error | null;
  isLoading: boolean;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  chart,
  className = '',
  onError,
}) => {
  const { t } = useTranslation('common');
  const uniqueId = useId();
  const chartId = `mermaid-${uniqueId.replace(/:/g, '')}`;
  const containerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<RenderState>({
    svg: null,
    error: null,
    isLoading: true,
  });

  const renderChart = useCallback(async () => {
    if (!chart || chart.trim() === '' || chart.trim() === 'graph TD') {
      setState({ svg: null, error: null, isLoading: false });
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Validate the chart syntax first
      await mermaid.parse(chart);

      // Render the chart - mermaid.render returns { svg: string }
      const { svg } = await mermaid.render(chartId, chart);

      setState({ svg, error: null, isLoading: false });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to render diagram');
      setState({ svg: null, error, isLoading: false });
      onError?.(error);
    }
  }, [chart, chartId, onError]);

  // useLayoutEffect ensures DOM is ready before rendering
  useLayoutEffect(() => {
    renderChart();
  }, [renderChart]);

  // Handle zoom/pan state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.min(Math.max(prev * delta, 0.25), 4));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const resetView = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Loading state
  if (state.isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          <span className="text-sm text-gray-500">{t('mermaid.renderingDiagram')}</span>
        </div>
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="font-medium text-red-800">{t('mermaid.failedToRender')}</h4>
            <p className="text-sm text-red-600 mt-1">{state.error.message}</p>
            <details className="mt-2">
              <summary className="text-xs text-red-500 cursor-pointer hover:text-red-600">
                {t('mermaid.showCode')}
              </summary>
              <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                {chart}
              </pre>
            </details>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!state.svg) {
    return (
      <div className={`flex items-center justify-center p-8 text-gray-500 ${className}`}>
        <span>{t('mermaid.noDiagram')}</span>
      </div>
    );
  }

  // Success - render the SVG
  return (
    <div className={`relative ${className}`}>
      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex gap-1 bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-1">
        <button
          onClick={() => setScale(prev => Math.min(prev * 1.2, 4))}
          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors"
          title={t('mermaid.zoomIn')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <button
          onClick={() => setScale(prev => Math.max(prev * 0.8, 0.25))}
          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors"
          title={t('mermaid.zoomOut')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        <button
          onClick={resetView}
          className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors"
          title={t('mermaid.resetView')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </button>
        <span className="px-2 text-xs text-gray-500 self-center border-l border-gray-200 ml-1">
          {Math.round(scale * 100)}%
        </span>
      </div>

      {/* Diagram container with pan/zoom */}
      <div
        ref={containerRef}
        className="overflow-hidden rounded-lg bg-white border border-gray-200 cursor-grab active:cursor-grabbing"
        style={{ minHeight: '400px' }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          className="mermaid-svg-container p-4"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
          }}
          dangerouslySetInnerHTML={{ __html: state.svg }}
        />
      </div>

      {/* Usage hint */}
      <p className="text-xs text-gray-400 mt-2 text-center">
        {t('mermaid.scrollZoom')}
      </p>
    </div>
  );
};

export default MermaidDiagram;
